"use client"

import { useMemo, useState } from "react"
import { useFavoritesStore } from "@/lib/favorites-store"
import { BooruGrid } from "@/components/booru-grid"
import { BooruDetailModal } from "@/components/booru-detail-modal"
import { Button } from "@/components/ui/button"
import { Download, Heart, ArrowDownUp } from "@/lib/icons"
import type { BooruPost } from "@/lib/booru-api"
import { BatchDownloadModal } from "@/components/batch-download-modal"
import { GridColumnsControl } from "@/components/grid-columns-control"
import { cn } from "@/lib/utils"
import { useGoonfolioGridColumns } from "@/hooks/use-goonfolio-grid-columns"
import { useTranslations } from "next-intl"

interface FavoritesBrowserProps {
  onOpenEditors?: () => void
}

type FavoriteSort = "recent" | "oldest" | "source" | "title"

const SORT_OPTIONS: { value: FavoriteSort; label: string }[] = [
  { value: "recent", label: "Recent" },
  { value: "oldest", label: "Oldest" },
  { value: "source", label: "Source" },
  { value: "title", label: "Title" },
]

function favoriteTitle(post: BooruPost) {
  return post.title || post.albumTitle || String(post.id ?? "")
}

export function FavoritesBrowser({ onOpenEditors }: FavoritesBrowserProps) {
  const t = useTranslations("favoritesBrowser")
  const { favoritePosts } = useFavoritesStore()
  const [selectedPost, setSelectedPost] = useState<BooruPost | null>(null)
  const [showBatchDownload, setShowBatchDownload] = useState(false)
  const [sort, setSort] = useState<FavoriteSort>("recent")
  const { gridColumns, setGridColumns, maxColumns } = useGoonfolioGridColumns()
  const sortedPosts = useMemo(() => {
    const indexed = favoritePosts.map((post, index) => ({ post, index }))
    indexed.sort((a, b) => {
      if (sort === "oldest") return a.index - b.index
      if (sort === "source") {
        return String(a.post.site ?? "").localeCompare(String(b.post.site ?? "")) || b.index - a.index
      }
      if (sort === "title") {
        return favoriteTitle(a.post).localeCompare(favoriteTitle(b.post)) || b.index - a.index
      }
      return b.index - a.index
    })
    return indexed.map(({ post }) => post)
  }, [favoritePosts, sort])

  return (
    <>
      <div className="flex flex-col bg-background">
        {/* Header */}
        <header className="glass-strong border-b border-border/50 shadow-sm">
          <div className="container mx-auto px-3 py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-1 flex items-center justify-between gap-3 min-w-0">
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold flex items-center gap-2">
                    <Heart className="h-5 w-5" />
                    {t("favorites")}
                  </h1>
                  <p className="text-sm text-muted-foreground">{t("lengthSavedImages", { length: favoritePosts.length })}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <GridColumnsControl gridColumns={gridColumns} setGridColumns={setGridColumns} maxColumns={maxColumns} />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowBatchDownload(true)}
                    className="h-8 w-8"
                    disabled={favoritePosts.length === 0}
                    title={t("download")}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="shrink-0 border-b bg-background/95 px-3 py-2">
          <div className="container mx-auto flex items-center gap-2 overflow-x-auto">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
              <ArrowDownUp className="h-3.5 w-3.5" />
              {t("sort")}
            </div>
            <div className="inline-flex rounded-lg border p-1 gap-1 shrink-0">
              {SORT_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  size="sm"
                  variant={sort === opt.value ? "default" : "ghost"}
                  onClick={() => setSort(opt.value)}
                  className={cn("h-7 px-2.5 text-xs", sort === opt.value && "shadow-sm")}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div>
          <div className="container mx-auto">
          {favoritePosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] p-4 text-center">
              <Heart className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground">{t("noFavoritesYet")}</p>
              <p className="text-xs text-muted-foreground">{t("addImagesToYourFavoritesToSeeThemHere")}</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <BooruGrid posts={sortedPosts} onPostClick={setSelectedPost} gridColumnsOverride={gridColumns} />
            </div>
          )}
          </div>
        </div>

        {/* Detail modal for fullscreen — always mounted (open prop controls visibility) */}
        <BooruDetailModal
          post={selectedPost}
          open={!!selectedPost}
          onOpenChange={(open) => !open && setSelectedPost(null)}
          canNavigate={{ prev: false, next: false }}
          onOpenEditors={onOpenEditors}
        />
      </div>

      <BatchDownloadModal
        isOpen={showBatchDownload}
        onClose={() => setShowBatchDownload(false)}
        posts={favoritePosts}
        source="favorites"
        sourceName="Favorites"
      />
    </>
  )
}
